import React, { useState, useEffect, useMemo, type ReactNode } from 'react';

import {
  ArrowRightOutlined,
  EditOutlined,
  InfoCircleOutlined,
  BoxPlotOutlined,
  GlobalOutlined,
  HomeOutlined,
  SwapOutlined,
  CheckOutlined,
  CloseOutlined,
  DatabaseOutlined,
  PrinterOutlined,
  BarcodeOutlined,
} from '@ant-design/icons';
import StorageModeContext from '@contexts/operator/StorageModeContext';
import useLiquidRendererImpl from '@hooks/shared/useLiquidRenderer';
import ApiLogsPanelImpl from '@shared/ApiLogsPanel';
import ListPageHeader from '@shared/ListPageHeader';
import { Can } from '@shared/permissions';
import { Card, Descriptions, Space, Button, Collapse, Tag, Timeline, Image, Empty, Alert, Modal, Select, InputNumber, message, Spin, Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { formatDateTime } from '../../../utils/dateTime';
import { diffPlainObjects, isPlainObject } from '../../../utils/historyFieldDiff';
import ProductSerialNumbersDrawer from './ProductSerialNumbersDrawer';

// Product loader 回傳 shape 跨 admin/operator 共用，且含許多動態欄位（custom_attributes、聚合等）
// 為避免 schema drift 與本元件耦合，這裡採 passthrough 風格 — 元件實際讀到的 key 由 runtime 決定。
interface ProductRecord {
  id: string;
  name: string;
  [key: string]: any;
}

interface LabelTemplate {
  id: string;
  name: string;
  is_default?: boolean;
  width?: number;
  height?: number;
  content?: string;
}

interface ProductAttributeDefinition {
  id: string;
  name: string;
  label: string;
  data_type: string;
}

interface ProductEditHistory {
  created_at: string;
  user?: { email: string } | null;
  operator?: { name: string } | null;
  fields_changed?: Record<string, unknown>;
}

interface ProductShowViewProps {
  /** 商品資料（由 loader 預載） */
  product: ProductRecord;
  /** 角色 (admin 或 operator) */
  role?: 'admin' | 'operator';
  /** Product Service（admin 為 .js module、operator 為 hook return） */
  productService: any;
  /** ProductLabelTemplate Service */
  labelTemplateService: any;
  /** 第三方 API Log Service */
  thirdPartyApiLogService?: any;
  /** Webhook Delivery Service */
  webhookDeliveryService?: any;
  /** Partner API Log Service */
  partnerApiLogService?: any;
  /** 添加背景任務到通知中心 */
  addJob?: (jobId: string, jobName: string) => void;
  /** WebSocket 連線管理器 */
  cableManager?: any;
  /** Merchant ID (admin 專用) */
  merchantId?: string;
  /** Organization ID (operator 專用) */
  organizationId?: string;
}

/**
 * ProductShowView - 通用商品詳情組件
 */
const ProductShowView: React.FC<ProductShowViewProps> = ({
  product,
  role = 'admin',
  productService,
  labelTemplateService,
  thirdPartyApiLogService,
  webhookDeliveryService,
  partnerApiLogService,
  addJob,
  cableManager,
  merchantId,
  organizationId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // useLiquidRenderer / ApiLogsPanel 仍是 .js / .jsx，JSDoc 把參數型別寫成 string / 單一參數，
  // 讓 TS 推導出錯誤 signature。在 component body 內 cast 為實際使用的型別來保留 runtime 行為。
  // (top-level cast 在 Vite HMR 下會 TDZ。)
  const useLiquidRenderer = useLiquidRendererImpl as (opts: { sampleData: unknown }) => {
    render: (content: string, data?: unknown) => Promise<string>;
  };
  const ApiLogsPanel = ApiLogsPanelImpl as React.ComponentType<any>;

  // 直接用 useContext 而非 useStorageMode hook：本元件同時被 admin 樹使用
  // （無 StorageModeProvider），useStorageMode 會 throw。null context 時兩個
  // flag 皆 false，相當於不顯示 shelf 區塊。
  const storageModeCtx = React.useContext(StorageModeContext) as { isDynamic?: boolean; isDedicated?: boolean } | null;
  const isStorageModeDynamic = !!storageModeCtx?.isDynamic;
  const isStorageModeDedicated = !!storageModeCtx?.isDedicated;

  // 自定義屬性定義
  const [productAttributeDefinitions, setProductAttributeDefinitions] = useState<ProductAttributeDefinition[]>([]);

  // 列印標籤相關狀態
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [showSerialNumbersDrawer, setShowSerialNumbersDrawer] = useState(false);
  const [templates, setTemplates] = useState<LabelTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [labelCopies, setLabelCopies] = useState<number>(1);
  const [generatingLabel, setGeneratingLabel] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // 預覽相關狀態
  const [previewHtml, setPreviewHtml] = useState('');
  const [renderingPreview, setRenderingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [apiLogsTabActive, setApiLogsTabActive] = useState(false);

  // 準備 Liquid 模板的 sample data（使用 useMemo 避免無限 render）
  const sampleData = useMemo(() => ({
    product: {
      sku: product.sku,
      name: product.name,
      barcode: product.barcode,
      category: product.product_category?.name,
      price: product.price,
      material: product.material,
      manufacturer_country: product.manufacturer_country,
      hs_code: product.hs_code,
      temperature: product.temperature,
      note: product.note,
      created_at: product.created_at,
      ...(product.custom_attributes || {}),
    },
    brand: product.brand ? {
      name: product.brand.name
    } : null,
    merchant: product.merchant ? {
      name: product.merchant.name,
      logo: product.merchant.logo
    } : null
  }), [product]);

  const { render } = useLiquidRenderer({ sampleData });

  // 加載商品自定義屬性定義
  useEffect(() => {
    const loadAttributeDefinitions = async () => {
      try {
        const data = await productService.getProductAttributeDefinitions(product.merchant_id);
        setProductAttributeDefinitions(data.data || []);
      } catch (error) {
        console.error('Failed to load product attribute definitions:', error);
      }
    };
    loadAttributeDefinitions();
  }, [productService, product.merchant_id]);

  // 當選擇模板時，渲染預覽
  useEffect(() => {
    const renderPreview = async () => {
      if (!selectedTemplateId) {
        setPreviewHtml('');
        setPreviewError(null);
        return;
      }

      const selectedTemplate = templates.find((tpl) => tpl.id === selectedTemplateId);
      if (!selectedTemplate || !selectedTemplate.content) {
        setPreviewHtml('');
        setPreviewError(null);
        return;
      }

      setRenderingPreview(true);
      setPreviewError(null);

      try {
        const html = await render(selectedTemplate.content, sampleData);
        setPreviewHtml(html);
      } catch (error) {
        console.error('Preview render error:', error);
        setPreviewError((error as Error).message);
      } finally {
        setRenderingPreview(false);
      }
    };

    renderPreview();
    // 只依賴 selectedTemplateId 和 templates，避免因 render/sampleData 變化觸發
    // render 和 sampleData 的變化應該不會影響已選模板的渲染結果
  }, [selectedTemplateId, templates, render, sampleData]);

  const handleEdit = () => {
    navigate(`/products/${product.id}/edit`);
  };

  const handleInventory = () => {
    navigate(`/products/${product.id}/inventory`);
  };

  // 打開列印標籤 Modal
  const handleOpenLabelModal = async () => {
    setShowLabelModal(true);
    setLoadingTemplates(true);

    try {
      // 獲取模板列表
      const params = role === 'operator' && product.merchant_id
        ? { merchant_id: product.merchant_id }
        : {};

      const response = await labelTemplateService.fetchList(params);

      if (response.success) {
        const templateList: LabelTemplate[] = response.data || [];
        // 將預設模板放在第一位
        const sortedTemplates = [...templateList].sort((a, b) => {
          if (a.is_default && !b.is_default) return -1;
          if (!a.is_default && b.is_default) return 1;
          return 0;
        });

        setTemplates(sortedTemplates);

        // 自動選擇預設模板
        const defaultTemplate = sortedTemplates.find((tpl) => tpl.is_default);
        if (defaultTemplate) {
          setSelectedTemplateId(defaultTemplate.id);
        } else if (sortedTemplates.length > 0) {
          setSelectedTemplateId(sortedTemplates[0].id);
        }
      } else {
        message.error(response.message || t('common.error.operation_failed'));
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
      message.error(t('common.error.operation_failed'));
    } finally {
      setLoadingTemplates(false);
    }
  };

  // 生成標籤
  const handleGenerateLabel = async () => {
    if (!selectedTemplateId) {
      message.warning(t('shared.products.detail.actions.please_select_template'));
      return;
    }

    try {
      setGeneratingLabel(true);
      setShowLabelModal(false);

      const data = await productService.generateLabel(product.id, selectedTemplateId, labelCopies);

      if (data.success) {
        const jobId = data.data.job_id;
        message.info(t('shared.products.detail.actions.generate_label_started'));

        // 將任務加入背景任務管理器
        if (addJob) {
          addJob(jobId, 'shared.products.detail.actions.generate_label_title');
        }

        // Subscribe to WebSocket for progress
        subscribeToJobProgress(jobId);
      } else {
        message.error(data.message || t('common.error.operation_failed'));
        setGeneratingLabel(false);
      }
    } catch (err) {
      console.error('Failed to generate label:', err);
      message.error(t('common.error.operation_failed'));
      setGeneratingLabel(false);
    }
  };

  // Subscribe to job progress via WebSocket
  const subscribeToJobProgress = (jobId: string) => {
    if (!cableManager) {
      console.error('Cable manager not provided');
      setGeneratingLabel(false);
      return;
    }

    const subscribeParams = role === 'admin' ? { merchant_id: merchantId } : {};

    const unsubscribe = cableManager.subscribe(
      'NotificationChannel',
      subscribeParams,
      {
        received: (data: any) => {
          // 只處理匹配的 job_id
          if (data.job_id !== jobId && data.job_id !== String(jobId)) {
            return;
          }

          if (data.status === 'completed') {
            // 任務完成（BackgroundJobManager 會透過 download_url 自動開啟）
            message.success(t('shared.products.detail.actions.generate_label_success'));
            setGeneratingLabel(false);
            if (unsubscribe) unsubscribe();
          } else if (data.status === 'failed') {
            // 任務失敗
            message.error(data.message || t('common.error.operation_failed'));
            setGeneratingLabel(false);
            if (unsubscribe) unsubscribe();
          }
        }
      }
    );
  };

  if (!product) {
    return (
      <Empty description={t('common.error.no_data')} />
    );
  }

  // 建立 Collapse items
  const collapseItems = [
    // 基本資訊
    {
      key: 'basic',
      label: (
        <Space>
          <InfoCircleOutlined />
          {t(`shared.products.detail.sections.basic_info`)}
        </Space>
      ),
      children: (
        <div style={{ display: 'flex', gap: '24px' }}>
          <div style={{ flex: 1 }}>
            <Descriptions column={2} bordered>
              {role === 'operator' && (
                <Descriptions.Item label={t('models.product.merchant')} span={2}>
                  {product.merchant?.name || '-'}
                </Descriptions.Item>
              )}
              <Descriptions.Item label={t('models.product.name')}>
                {product.name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('models.product.sku')}>
                {product.sku || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('models.product.barcode')}>
                {product.barcode || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('models.product.secondary_barcodes')}>
                {product.secondary_barcodes && product.secondary_barcodes.length > 0
                  ? product.secondary_barcodes.map((barcode: string, idx: number) => (
                    <Tag key={idx}>{barcode}</Tag>
                  ))
                  : '-'}
              </Descriptions.Item>
              {role === 'operator' && isStorageModeDedicated && (
                <Descriptions.Item label={t('models.product.default_shelf')}>
                  {product.default_shelf?.location || '-'}
                </Descriptions.Item>
              )}
              <Descriptions.Item label={t('models.product.temperature')}>
                {product.temperature ? t(`common.shelf_temperature.${product.temperature}`) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('models.product.category')}>
                {product.product_category?.name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('models.product.brand')}>
                {product.brand?.name || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('models.product.validity_days')}>
                {product.validity_days ? `${product.validity_days} ${t('common.unit.days')}` : '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('models.product.oversea_imported')}>
                {product.oversea_imported ? (
                  <CheckOutlined style={{ color: 'green' }} />
                ) : (
                  <CloseOutlined style={{ color: 'lightgrey' }} />
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('models.product.manufacturing_date_required')}>
                {product.manufacturing_date_required ? (
                  <CheckOutlined style={{ color: 'green' }} />
                ) : (
                  <CloseOutlined style={{ color: 'lightgrey' }} />
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('models.product.batch_management_required')}>
                {product.batch_management_required ? (
                  <CheckOutlined style={{ color: 'green' }} />
                ) : (
                  <CloseOutlined style={{ color: 'lightgrey' }} />
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('models.product.activated')}>
                {product.activated ? (
                  <Tag color="success" icon={<CheckOutlined />}>
                    {t('shared.products.form.activated_true')}
                  </Tag>
                ) : (
                  <Tag color="default" icon={<CloseOutlined />}>
                    {t('shared.products.form.activated_false')}
                  </Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('models.product.required_serial_numbers_count')}>
                {product.required_serial_numbers_count || 0}
              </Descriptions.Item>
              {product.required_serial_numbers_count > 0 && (
                <>
                  <Descriptions.Item label={t('models.product.box_serial_number_delimiter')}>
                    {product.box_serial_number_delimiter || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('models.product.serial_number_pattern')}>
                    {product.serial_number_pattern ? (
                      <code style={{ backgroundColor: '#f5f5f5', padding: '2px 6px', borderRadius: '3px' }}>
                        {product.serial_number_pattern}
                      </code>
                    ) : '-'}
                  </Descriptions.Item>
                </>
              )}
              {/* 自定義屬性 — 行為跟內建欄位一致：定義過的屬性永遠列出，沒值顯示 '-' */}
              {productAttributeDefinitions.length > 0 && productAttributeDefinitions.map((def) => {
                const value = product.custom_attributes?.[def.name];
                const isEmpty = value === null || value === undefined || value === '';

                let displayValue: ReactNode;
                if (isEmpty) {
                  displayValue = '-';
                } else if (def.data_type === 'boolean') {
                  displayValue = value
                    ? <CheckOutlined style={{ color: 'green' }} />
                    : <CloseOutlined style={{ color: 'lightgrey' }} />;
                } else {
                  displayValue = String(value);
                }

                return (
                  <Descriptions.Item key={def.id} label={def.label}>
                    {displayValue}
                  </Descriptions.Item>
                );
              })}
              <Descriptions.Item label={t('models.product.note')} span={2}>
                {product.note || '-'}
              </Descriptions.Item>
            </Descriptions>
          </div>
          {/* 商品圖片 */}
          {product.public_photo_url && (
            <div style={{ width: '200px' }}>
              <Image
                src={product.public_photo_url}
                alt={product.name}
                style={{ width: '100%', borderRadius: '8px' }}
              />
            </div>
          )}
        </div>
      ),
    },
    // 單位資訊
    {
      key: 'unit',
      label: (
        <Space>
          <BoxPlotOutlined />
          {t(`shared.products.detail.sections.unit_info`)}
        </Space>
      ),
      children: (
        <>
          <Descriptions column={2} bordered>
            <Descriptions.Item label={t('models.product.small_unit_name')} span={2}>
              {product.small_unit_name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('models.product.length')}>
              {product.length ? `${product.length} cm` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('models.product.width')}>
              {product.width ? `${product.width} cm` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('models.product.height')}>
              {product.height ? `${product.height} cm` : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('models.product.weight_kg')}>
              {product.weight_kg ? `${product.weight_kg} kg` : '-'}
            </Descriptions.Item>
          </Descriptions>
          <Descriptions column={2} bordered style={{ marginTop: '16px' }}>
            <Descriptions.Item label={t('models.product.mid_unit_name')}>
              {product.mid_unit_name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('models.product.small_quantity_per_mid_unit')}>
              {product.small_quantity_per_mid_unit || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('models.product.mid_unit_barcode')} span={2}>
              {product.mid_unit_barcode || '-'}
            </Descriptions.Item>
          </Descriptions>
          <Descriptions column={2} bordered style={{ marginTop: '16px' }}>
            <Descriptions.Item label={t('models.product.large_unit_name')}>
              {product.large_unit_name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('models.product.small_quantity_per_large_unit')}>
              {product.small_quantity_per_large_unit || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('models.product.large_unit_barcode')} span={2}>
              {product.large_unit_barcode || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('models.product.box_length')}>
              {product.box_length ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('models.product.box_width')}>
              {product.box_width ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('models.product.box_height')}>
              {product.box_height ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('models.product.box_weight_kg')}>
              {product.box_weight_kg ?? '-'}
            </Descriptions.Item>
          </Descriptions>
          <Descriptions column={1} bordered style={{ marginTop: '16px' }}>
            <Descriptions.Item label={t('models.product.total_large_units_per_pallet')}>
              <span style={{ fontSize: 16, fontWeight: 600 }}>
                {product.total_large_units_per_pallet || '-'}
              </span>
              {product.large_unit_per_layer > 0 && product.layers_per_pallet > 0 && (
                <span style={{ marginLeft: 12, fontSize: 12, color: '#999' }}>
                  {t('shared.products.form.pallet_stacking_section')}:
                  {' '}
                  {t('shared.products.form.pallet_stacking_result', {
                    ti: product.large_unit_per_layer,
                    hi: product.layers_per_pallet,
                    total: product.large_unit_per_layer * product.layers_per_pallet
                  })}
                  {product.total_large_units_per_pallet != null &&
                    product.total_large_units_per_pallet !== product.large_unit_per_layer * product.layers_per_pallet && (
                      <>
                        {' '}
                        <Tag color="orange" style={{ marginLeft: 4 }}>{t('shared.products.form.pallet_overridden')}</Tag>
                      </>
                    )}
                </span>
              )}
            </Descriptions.Item>
          </Descriptions>
        </>
      ),
    },
    // 出口資訊
    {
      key: 'export',
      label: (
        <Space>
          <GlobalOutlined />
          {t(`shared.products.detail.sections.export_info`)}
        </Space>
      ),
      children: (
        <Descriptions column={2} bordered>
          <Descriptions.Item label={t('models.product.customs_declare_product_title')}>
            {product.customs_declare_product_title || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.product.manufacturer_country')}>
            {product.manufacturer_country || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.product.hs_code')}>
            {product.hs_code || '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.product.material')}>
            {product.material || '-'}
          </Descriptions.Item>
        </Descriptions>
      ),
    },
    // 替代品資訊
    {
      key: 'substitutes',
      label: (
        <Space>
          <SwapOutlined />
          {t(`shared.products.detail.sections.substitutes_info`)}
        </Space>
      ),
      children: product.product_substitutes && product.product_substitutes.length > 0 ? (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          {product.product_substitutes.map((substitute: { candidate?: { name?: string; sku?: string } }, index: number) => (
            <Card key={index} size="small" type="inner">
              <Descriptions column={1} size="small">
                <Descriptions.Item label={t('models.product.name')}>
                  {substitute.candidate?.name || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('models.product.sku')}>
                  {substitute.candidate?.sku || '-'}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          ))}
        </Space>
      ) : (
        <Empty description={t(`shared.products.detail.no_substitutes`)} />
      ),
    },
  ];

  // 貨架資訊：dynamic 倉才獨立顯示為一個面板
  // dedicated 倉的 default_shelf 已內嵌在 basic_info 區塊
  // virtual 倉不顯示
  if (role === 'operator' && isStorageModeDynamic) {
    collapseItems.splice(5, 0, {
      key: 'shelves',
      label: (
        <Space>
          <HomeOutlined />
          {t(`shared.products.detail.sections.shelves_info`)}
        </Space>
      ),
      children: (
        <Descriptions column={2} bordered>
          <Descriptions.Item label={t('models.product.default_shelf')}>
            {product.default_shelf?.location || '-'}
          </Descriptions.Item>
        </Descriptions>
      ),
    });
  }

  const basePath = role === 'operator' ? '/operator' : '/admin';

  const headerActions = (
    <Space>
      <Can requiredPermission="inventories:read">
        <Button icon={<DatabaseOutlined />} onClick={handleInventory}>
          {t('shared.products.button.inventory')}
        </Button>
      </Can>
      {product.required_serial_numbers_count > 0 && (
        <Can requiredPermission="products:read">
          <Button
            icon={<BarcodeOutlined />}
            onClick={() => setShowSerialNumbersDrawer(true)}
          >
            {t('shared.products.serial_numbers.title')}
          </Button>
        </Can>
      )}
      <Can requiredPermission="products:read">
        <Button
          icon={<PrinterOutlined />}
          onClick={handleOpenLabelModal}
          loading={generatingLabel}
          disabled={!labelTemplateService}
        >
          {t('shared.products.detail.actions.print_label')}
        </Button>
      </Can>
      <Can requiredPermission="products:write">
        <Button type="primary" icon={<EditOutlined />} onClick={handleEdit}>
          {t('common.button.edit')}
        </Button>
      </Can>
    </Space>
  );

  return (
    <>
      <ListPageHeader
        basePath={basePath}
        items={['products', 'products.list', { label: product.name }]}
        actions={headerActions}
      />
      <Collapse defaultActiveKey={['basic', 'unit']} items={collapseItems} />

      {/* 編輯歷史 & 串接紀錄 */}
      <Card style={{ marginTop: '16px' }} size="small">
        <Tabs
          defaultActiveKey="history"
          size="small"
          onChange={(key) => {
            if (key === 'api_logs' && !apiLogsTabActive) {
              setApiLogsTabActive(true)
            }
          }}
          items={[
            {
              key: 'history',
              label: t('shared.products.detail.sections.edit_history'),
              children: product.product_edit_histories && product.product_edit_histories.length > 0 ? (
                <Timeline mode="left">
                  {product.product_edit_histories.map((history: ProductEditHistory, index: number) => (
                    <Timeline.Item key={index} label={formatDateTime(history.created_at)}>
                      <Card size="small" type="inner">
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                          <div>
                            {history.user ? (
                              <Tag color="blue">{history.user.email}</Tag>
                            ) : history.operator ? (
                              <Tag color="orange">
                                {t('models.product.warehouse_staff')}: {history.operator.name}
                              </Tag>
                            ) : (
                              <Tag color="default">{t('models.product.api')}</Tag>
                            )}
                          </div>
                          {history.fields_changed &&
                            Object.keys(history.fields_changed).map((field, fieldIndex) => {
                              // Support both old format (plain string) and new format ([old, new] array)
                              const raw = (history.fields_changed as Record<string, unknown>)[field];
                              const changes: [unknown, unknown] = Array.isArray(raw) ? [raw[0], raw[1]] : [null, raw];

                              // 格式化顯示值（處理 boolean 等特殊類型）
                              const formatValue = (value: unknown): ReactNode => {
                                if (value === null || value === undefined || value === '') return '-';
                                if (typeof value === 'boolean') {
                                  return value ? (
                                    <CheckOutlined style={{ color: 'green' }} />
                                  ) : (
                                    <CloseOutlined style={{ color: 'lightgrey' }} />
                                  );
                                }
                                return String(value);
                              };

                              const translateValue = (val: unknown): unknown => {
                                if (val === null || val === undefined || val === '') return val;
                                const sentinel = `__missing_${field}_${val}__`;
                                const translated = t(`models.product.${field}_values.${val}`, { defaultValue: sentinel });
                                return translated !== sentinel ? translated : val;
                              };

                              const fieldLabel = t(`models.product.${field}`, { defaultValue: field });

                              // 副條碼異動：後端寫 { added: [...], removed: [...] } 而非 [old, new]，
                              // 要逐項列出新增 / 刪除 chip。判準是「plain object 且含 added 或 removed
                              // 兩個 array key」，避免誤判 jsonb hash diff（後者沒有這兩個固定 key）。
                              const barcodeDiff = isPlainObject(raw) ? raw : null;
                              const isBarcodeDiff =
                                barcodeDiff !== null &&
                                (Array.isArray(barcodeDiff.added) || Array.isArray(barcodeDiff.removed));
                              if (isBarcodeDiff && barcodeDiff) {
                                const added = Array.isArray(barcodeDiff.added) ? barcodeDiff.added : [];
                                const removed = Array.isArray(barcodeDiff.removed) ? barcodeDiff.removed : [];
                                return (
                                  <div key={fieldIndex}>
                                    <strong>{fieldLabel}:</strong>
                                    <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                      {removed.map((barcode: unknown, i: number) => (
                                        <Tag key={`removed-${i}`} color="#ff4d4f">
                                          [{t('common.button.delete')}] {String(barcode)}
                                        </Tag>
                                      ))}
                                      {added.map((barcode: unknown, i: number) => (
                                        <Tag key={`added-${i}`} color="#52c41a">
                                          [{t('common.button.add')}] {String(barcode)}
                                        </Tag>
                                      ))}
                                    </div>
                                  </div>
                                );
                              }

                              // jsonb 欄位（如 custom_attributes）在 ActiveRecord changes 裡是
                              // 整包 hash 的 [old, new]；逐 key 展開才看得出真正改了哪些屬性，
                              // 否則 String(hash) 會 stringify 成 "[object Object]"。
                              if (isPlainObject(changes[0]) || isPlainObject(changes[1])) {
                                const entries = diffPlainObjects(
                                  isPlainObject(changes[0]) ? changes[0] : null,
                                  isPlainObject(changes[1]) ? changes[1] : null,
                                );

                                // 沒有實質差異（罕見，但 AR 有時會把語意相同的 hash 標為 dirty）：
                                // fallback 到單列空值顯示，至少不要爆 [object Object]。
                                if (entries.length === 0) {
                                  return (
                                    <div key={fieldIndex}>
                                      <strong>{fieldLabel}:</strong>
                                      <div style={{ marginTop: '4px', color: 'lightgrey' }}>
                                        {t('shared.products.detail.history_no_field_change', { defaultValue: '(無實質變更)' })}
                                      </div>
                                    </div>
                                  );
                                }

                                // 用 productAttributeDefinitions 把 raw key 翻成使用者看得懂的 label
                                const labelFor = (key: string): string => {
                                  const def = productAttributeDefinitions.find((d) => d.name === key);
                                  return def?.label || def?.name || key;
                                };

                                return (
                                  <div key={fieldIndex}>
                                    <strong>{fieldLabel}:</strong>
                                    <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      {entries.map((entry) => (
                                        <div key={entry.key} style={{ paddingLeft: '12px' }}>
                                          <span style={{ marginRight: '8px' }}>{labelFor(entry.key)}:</span>
                                          <Tag style={{ color: 'lightgrey' }}>{formatValue(entry.oldValue)}</Tag>
                                          <ArrowRightOutlined style={{ margin: '0 8px' }} />
                                          <Tag color="green">{formatValue(entry.newValue)}</Tag>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              }

                              return (
                                <div key={fieldIndex}>
                                  <strong>{fieldLabel}:</strong>
                                  <div style={{ marginTop: '4px' }}>
                                    <Tag style={{ color: 'lightgrey' }}>{formatValue(translateValue(changes[0]))}</Tag>
                                    <ArrowRightOutlined style={{ margin: '0 8px' }} />
                                    <Tag color="green">{formatValue(translateValue(changes[1]))}</Tag>
                                  </div>
                                </div>
                              );
                            })}
                        </Space>
                      </Card>
                    </Timeline.Item>
                  ))}
                </Timeline>
              ) : (
                <Empty description={t('shared.products.detail.no_edit_history')} />
              ),
            },
            {
              key: 'api_logs',
              label: t('common.api_logs.title'),
              children: apiLogsTabActive ? (
                <ApiLogsPanel
                  loggableType="Product"
                  loggableId={product.id}
                  thirdPartyApiLogService={thirdPartyApiLogService}
                  webhookDeliveryService={webhookDeliveryService}
                  partnerApiLogService={partnerApiLogService}
                />
              ) : null,
            },
          ]}
        />
      </Card>

      {/* 列印標籤 Modal */}
      <Modal
        title={t('shared.products.detail.actions.select_label_template')}
        open={showLabelModal}
        onCancel={() => setShowLabelModal(false)}
        footer={null}
        width={1000}
      >
        <div style={{ display: 'flex', gap: '24px', minHeight: '400px' }}>
          {/* 左側：預覽區域 */}
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            <div style={{ fontWeight: 500, fontSize: '14px' }}>
              {t('shared.products.detail.actions.label_preview')}
            </div>
            <div style={{
              flex: 1,
              padding: '24px',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              border: '1px solid #d9d9d9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '350px'
            }}>
              {!selectedTemplateId ? (
                <Empty
                  description={t('shared.products.detail.actions.please_select_template')}
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ) : (() => {
                const selectedTemplate = templates.find((tpl) => tpl.id === selectedTemplateId);
                if (!selectedTemplate) {
                  return (
                    <Empty
                      description={t('shared.products.detail.actions.please_select_template')}
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  );
                }

                return (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '16px',
                    width: '100%',
                    height: '100%'
                  }}>
                    {/* 模板資訊標題 */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 16px',
                      backgroundColor: 'white',
                      borderRadius: '6px',
                      border: '1px solid #d9d9d9'
                    }}>
                      <span style={{ fontSize: '14px', fontWeight: 500, color: '#333' }}>
                        {selectedTemplate.name}
                      </span>
                      {selectedTemplate.is_default && (
                        <Tag color="blue" {...({ size: 'small' } as any)}>
                          {t('models.product_label_template.default')}
                        </Tag>
                      )}
                      {selectedTemplate.width && selectedTemplate.height && (
                        <span style={{ fontSize: '12px', color: '#999', marginLeft: '8px' }}>
                          ({selectedTemplate.width} × {selectedTemplate.height} cm)
                        </span>
                      )}
                    </div>

                    {/* 標籤預覽 */}
                    <div style={{
                      flex: 1,
                      width: '100%',
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      border: '2px solid #1890ff',
                      overflow: 'auto',
                      padding: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: '300px'
                    }}>
                      {renderingPreview ? (
                        <Spin tip={t('common.loading')} />
                      ) : previewError ? (
                        <Alert
                          message={t('shared.products.detail.actions.render_error')}
                          description={previewError}
                          type="error"
                          showIcon
                        />
                      ) : previewHtml ? (
                        <div
                          style={{
                            transform: 'scale(0.8)',
                            transformOrigin: 'center center',
                            maxWidth: '100%'
                          }}
                          dangerouslySetInnerHTML={{ __html: previewHtml }}
                        />
                      ) : (
                        <Empty
                          description={t('shared.products.detail.no_label_for_template')}
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                      )}
                    </div>

                    {/* 提示文字 */}
                    <div style={{ fontSize: '12px', color: '#999', textAlign: 'center' }}>
                      {t('shared.products.detail.actions.label_preview_hint')}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* 右側：設定區域 */}
          <div style={{
            width: '320px',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          }}>
            <div>
              <div style={{ marginBottom: '8px', fontWeight: 500 }}>
                {t('shared.products.detail.actions.template')}
              </div>
              <Select
                style={{ width: '100%' }}
                placeholder={t('shared.products.detail.actions.please_select_template')}
                value={selectedTemplateId}
                onChange={setSelectedTemplateId}
                loading={loadingTemplates}
                size="large"
                options={templates.map((template) => ({
                  label: (
                    <span>
                      {template.name}
                      {template.is_default && (
                        <Tag color="blue" style={{ marginLeft: '8px' }}>
                          {t('models.product_label_template.default')}
                        </Tag>
                      )}
                    </span>
                  ),
                  value: template.id,
                }))}
              />
            </div>

            <div>
              <div style={{ marginBottom: '8px', fontWeight: 500 }}>
                {t('shared.products.detail.actions.copies')}
              </div>
              <InputNumber
                min={1}
                max={100}
                value={labelCopies}
                onChange={setLabelCopies as any}
                size="large"
                style={{ width: '100%' }}
              />
            </div>

            <div style={{ marginTop: 'auto', paddingTop: '24px' }}>
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Button
                  type="primary"
                  size="large"
                  block
                  icon={<PrinterOutlined />}
                  onClick={handleGenerateLabel}
                  loading={generatingLabel}
                  disabled={!selectedTemplateId}
                >
                  {t('shared.products.detail.actions.print_label')}
                </Button>
                <Button
                  size="large"
                  block
                  onClick={() => setShowLabelModal(false)}
                >
                  {t('common.button.cancel')}
                </Button>
              </Space>
            </div>
          </div>
        </div>
      </Modal>

      <ProductSerialNumbersDrawer
        open={showSerialNumbersDrawer}
        onClose={() => setShowSerialNumbersDrawer(false)}
        product={product}
        productService={productService}
        role={role}
      />
    </>
  );
};

export default ProductShowView;
